import moment from 'moment'
import {
  MemberProfile,
  PointsHistory,
  PointsSummary,
  PointsChartResponse,
  ChartSeriesEntry,
  TierDetail,
  RedeemCheck,
  PointsConversion,
  PointsEntry,
  Rate,
  RewardsBalance,
  RewardsEarningsResponse,
  RewardsEarningEntry,
  RewardsRedeemCheck,
  RewardsRatesResponse,
} from './types'
import api from '../../../../../utils/api'

export class VenusApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'VenusApiError'
    this.status = status
  }
}

// THPRT-6468: the loyalty programme is reviewed over a rolling 90-day window
// (THPRT-6332). The rewards gateway does not return the review period, so the value is
// applied here when the tier detail is derived from /balance.
const REWARDS_TIER_REVIEW_DAYS = 90

export interface MemberLoyaltyBundle {
  member: MemberProfile
  summary: PointsSummary
  tierDetail: TierDetail
}

const mapBalanceToMember = (b: RewardsBalance): MemberProfile => ({
  id: 0,
  externalId: '',
  email: '',
  firstName: null,
  lastName: null,
  name: null,
  platform: '',
  platforms: [],
  tier: b.tier.name,
  tierPointsTotal: b.tierProgress.points,
  tierPointsAvailable: 0,
  rewardPointsTotal: b.rewardPoints.available,
  rewardPointsAvailable: b.rewardPoints.available,
  tierProgress: 0,
  nextTier: null,
  pointsToNextTier: 0,
  cashValueAvailable: 0,
  tierReviewAt: '',
  enrolledAt: b.member.enrolledAt,
  status: b.member.status,
  organization: null,
  country: null,
})

const mapBalanceToSummary = (b: RewardsBalance): PointsSummary => ({
  tierPoints: { earnedThisMonth: 0, earnedLast90Days: 0, total: b.tierProgress.points },
  rewardPoints: {
    earnedThisMonth: 0,
    earnedLast90Days: 0,
    redeemedTotal: b.redeemed.total,
    expiredTotal: 0,
    pendingRedemptions: 0,
    total: b.rewardPoints.available,
  },
})

const mapBalanceToTierDetail = (b: RewardsBalance): TierDetail => ({
  currentTier: b.tier.name,
  tierReviewAt: '',
  earnedInReviewPeriod: b.tierProgress.points,
  reviewPeriodDays: REWARDS_TIER_REVIEW_DAYS,
  tiers: [],
  recentHistory: [],
})

const mapEarningToEntry = (
  e: RewardsEarningEntry,
  index: number,
  page: number,
  limit: number
): PointsEntry => ({
  id: (page - 1) * limit + index,
  currencyType: e.currencyType,
  type: e.type,
  amount: e.amount,
  balanceAfter: e.balanceAfter,
  loyaltyEventType: e.type,
  description: e.type,
  tradeId: e.tradeId,
  symbol: e.symbol,
  lots: e.lots,
  platform: null,
  expiresAt: null,
  createdAt: e.createdAt,
})

// The gateway returns pointsPerLot already projected onto the caller's tier, so the same
// value fills every tier column. The calculator's tier selector therefore has no effect
// on the gateway path (see report note on the calculator gap).
const mapRatesResponse = (res: RewardsRatesResponse): Rate[] =>
  (res.rates || []).map((r, i) => ({
    id: i,
    platform: r.platform || res.platform || '',
    assetClass: r.assetClass,
    symbol: r.symbol,
    classic: r.pointsPerLot,
    silver: r.pointsPerLot,
    gold: r.pointsPerLot,
    platinum: r.pointsPerLot,
    diamond: r.pointsPerLot,
  }))

// Paged pull of the reward-points ledger over a date window, used to build the chart
// client-side since the gateway has no chart endpoint.
async function fetchRewardEarningsWindow(
  fromISO: string,
  toISO: string
): Promise<{ entries: RewardsEarningEntry[]; truncated: boolean }> {
  const limit = 100
  // Page to the last page rather than a fixed 1000-row cap (which silently understated active
  // traders). The ceiling stays under the gateway's 60 requests/minute limit; if it is hit the
  // caller is told the series is truncated so the chart can flag it rather than understate.
  const SAFETY_MAX_PAGES = 25
  const all: RewardsEarningEntry[] = []
  let truncated = false
  for (let page = 1; page <= SAFETY_MAX_PAGES; page++) {
    const { body } = await api.rewardsGet<RewardsEarningsResponse>(
      `/earnings?page=${page}&limit=${limit}&currency=reward&from=${fromISO}&to=${toISO}`
    )
    const entries = body.entries || []
    all.push(...entries)
    if (entries.length < limit) break
    if (typeof body.total === 'number' && all.length >= body.total) break
    if (page === SAFETY_MAX_PAGES) truncated = true
  }
  return { entries: all, truncated }
}

async function deriveChartFromEarnings(
  period: 'monthly' | 'weekly',
  months: number
): Promise<PointsChartResponse> {
  const from = period === 'weekly'
    ? moment().subtract(months - 1, 'weeks').startOf('isoWeek')
    : moment().subtract(months - 1, 'months').startOf('month')
  const { entries, truncated } = await fetchRewardEarningsWindow(from.format('YYYY-MM-DD'), moment().format('YYYY-MM-DD'))

  const sums: Record<string, number> = {}
  for (const e of entries) {
    if (e.amount <= 0) continue
    const key = period === 'weekly'
      ? moment(e.createdAt).startOf('isoWeek').format('YYYY-MM-DD')
      : moment(e.createdAt).format('YYYY-MM')
    sums[key] = (sums[key] || 0) + e.amount
  }

  const series: ChartSeriesEntry[] = Object.keys(sums).map(key => ({
    date: period === 'weekly' ? key : `${key}-01`,
    tierEarned: 0,
    rewardEarned: sums[key],
    rewardRedeemed: 0,
    rewardExpired: 0,
    tierBonus: 0,
    rewardBonus: 0,
  }))

  return { period, months, series, truncated }
}

export const venusApi = {
  getMember: async (): Promise<MemberProfile> => {
    const { status, body } = await api.rewardsGet<RewardsBalance>('/balance', { acceptStatus: [404] })
    if (status === 404) throw new VenusApiError(404, 'Member not found')
    return mapBalanceToMember(body)
  },

  // Single loyalty read for the rewards page: one /balance call mapped to member, summary
  // and tier detail. A 404 or a non-active member means the caller has no programme,
  // returned as null (no error surface).
  getMemberLoyalty: async (): Promise<MemberLoyaltyBundle | null> => {
    const { status, body } = await api.rewardsGet<RewardsBalance>('/balance', { acceptStatus: [404] })
    if (status === 404 || body?.member?.status !== 'active') return null
    return {
      member: mapBalanceToMember(body),
      summary: mapBalanceToSummary(body),
      tierDetail: mapBalanceToTierDetail(body),
    }
  },

  getPoints: async (
    page = 1,
    limit = 20,
    currency = 'all',
    startDate?: string,
    endDate?: string
  ): Promise<PointsHistory> => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('limit', String(limit))
    params.set('currency', currency)
    if (startDate) params.set('from', startDate)
    if (endDate) params.set('to', endDate)
    const { body } = await api.rewardsGet<RewardsEarningsResponse>(`/earnings?${params.toString()}`)
    return {
      entries: (body.entries || []).map((e, i) => mapEarningToEntry(e, i, body.page, body.limit)),
      page: body.page,
      limit: body.limit,
      total: body.total,
    }
  },

  getPointsChart: async (
    period: 'monthly' | 'weekly' = 'monthly',
    months = 7
  ): Promise<PointsChartResponse> => {
    return deriveChartFromEarnings(period, months)
  },

  canRedeem: async (points: number): Promise<RedeemCheck> => {
    const { body } = await api.rewardsGet<RewardsRedeemCheck>(`/can-redeem?points=${points}`)
    return {
      canRedeem: body.canRedeem,
      points: body.points,
      cashValue: body.cashValue,
      cashRate: body.cashRate,
      reasons: body.reasons || [],
    }
  },

  convertPoints: async (points: number): Promise<PointsConversion> => {
    const { body } = await api.rewardsGet<RewardsRedeemCheck>(`/can-redeem?points=${points}`)
    // Value the hypothetical amount from the rate, independently of the member's redeemable
    // balance, so the calculator projects correctly for amounts above the current balance.
    const rate = body.cashRate
    const cashValue = Number.isFinite(rate) ? Number((points * rate).toFixed(2)) : body.cashValue
    return { points, cashValue, currency: 'USD', rate }
  },

  getRates: async (): Promise<Rate[]> => {
    const { body } = await api.rewardsGet<RewardsRatesResponse>('/rates')
    return mapRatesResponse(body)
  },
}
